import { devLogger } from "@/utils/dev-logger.util";
/** Abort fetch if the server does not finish within this window (aligned with server proxy/IIS limits). */
const DOWNLOAD_TIMEOUT_MS = 5 * 60 * 1000;
/** Minimum spinner visibility so fast downloads still show feedback. */
const MIN_SPINNER_VISIBLE_MS = 500;
function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/** Wait for the next paint so spinner DOM changes are visible before async work. */
function waitForNextPaint() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
}
function resolveUrl(url) {
    try {
        return new URL(url, window.location.href).href;
    }
    catch {
        devLogger.log("MultiLanguageDocumentDownload: invalid URL", url);
        return null;
    }
}
/** Mirror server-side UrlHelper.ResolveDamUrl — route CMP CDN URLs through /optidam. */
function rewriteDamUrlToOptiDam(url) {
    if (url.startsWith("/optidam/") || url === "/optidam") {
        return url;
    }
    const match = url.match(/^(?:https?:)?\/\/images\d*\.cmp\.optimizely\.com/i);
    if (!match || match.index !== 0) {
        return url;
    }
    return `/optidam${url.substring(match[0].length)}`;
}
function prepareDownloadUrl(url) {
    return resolveUrl(rewriteDamUrlToOptiDam(url.trim()));
}
/** Brief pause after an aborted fetch so the browser can close the connection before anchor fallback. */
const ANCHOR_FALLBACK_SETTLE_MS = 300;
function getFilenameFromUrl(resolvedUrl) {
    try {
        const pathname = new URL(resolvedUrl).pathname;
        const segments = pathname.split("/").filter(Boolean);
        const last = segments.pop() ?? "download";
        return decodeURIComponent(last.split("?")[0]);
    }
    catch {
        return "download";
    }
}
const MIME_EXTENSION_MAP = {
    "application/pdf": ".pdf",
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "application/vnd.ms-powerpoint": ".ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
    "text/plain": ".txt",
};
function hasFileExtension(filename) {
    const base = filename.split("/").pop() ?? filename;
    const dot = base.lastIndexOf(".");
    return dot > 0 && dot < base.length - 1;
}
function ensureFilenameExtension(filename, contentType) {
    if (hasFileExtension(filename)) {
        return filename;
    }
    const mime = contentType?.split(";")[0].trim().toLowerCase();
    const extension = mime ? MIME_EXTENSION_MAP[mime] : undefined;
    return extension ? `${filename}${extension}` : filename;
}
function extractFilenameFromDisposition(response) {
    const disposition = response.headers.get("Content-Disposition");
    if (!disposition) {
        return null;
    }
    const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(disposition);
    if (utf8) {
        try {
            return decodeURIComponent(utf8[1]);
        }
        catch {
            /* fall through */
        }
    }
    const ascii = /filename="?([^";\n]+)"?/i.exec(disposition);
    if (ascii) {
        return ascii[1].replace(/['"]/g, "").trim();
    }
    return null;
}
function resolveDownloadFilename(response, resolvedUrl, preferredFilename) {
    const contentType = response?.headers.get("Content-Type") ?? null;
    if (preferredFilename?.trim()) {
        return ensureFilenameExtension(preferredFilename.trim(), contentType);
    }
    if (response) {
        const fromDisposition = extractFilenameFromDisposition(response);
        if (fromDisposition) {
            return ensureFilenameExtension(fromDisposition, contentType);
        }
    }
    return ensureFilenameExtension(getFilenameFromUrl(resolvedUrl), contentType);
}
/** Direct anchor fallback — browser streams the file; `download` suggests the save filename. */
function clickAnchorFallback(url, filename) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename ?? getFilenameFromUrl(url);
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
}
/**
 * Read the response body via a stream instead of response.blob().
 * Keeps abort handling granular during large transfers; UI stays a plain spinner.
 */
async function readResponseBodyAsBlob(response, signal) {
    if (!response.body) {
        return response.blob();
    }
    const reader = response.body.getReader();
    const chunks = [];
    const onAbort = () => {
        void reader.cancel();
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    try {
        for (;;) {
            if (signal?.aborted) {
                throw new DOMException("The operation was aborted.", "AbortError");
            }
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            chunks.push(value);
        }
    }
    finally {
        signal?.removeEventListener("abort", onAbort);
    }
    const contentType = response.headers.get("Content-Type")?.split(";")[0].trim();
    return new Blob(chunks, contentType ? { type: contentType } : undefined);
}
function saveBlobAsDownload(blob, filename) {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}
/**
 * Fetch the file via a streaming body read, save via blob URL, and resolve when complete.
 * Falls back to a direct anchor click if fetch fails (timeout, network, HTTP error).
 */
async function fetchAndSaveDownload(resolvedUrl, signal, preferredFilename) {
    const response = await fetch(resolvedUrl, { credentials: "same-origin", signal });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    const blob = await readResponseBodyAsBlob(response, signal);
    const filename = resolveDownloadFilename(response, resolvedUrl, preferredFilename);
    saveBlobAsDownload(blob, filename);
}
/**
 * Primary: fetch → blob save (spinner tracks completion / timeout / errors).
 * Fallback: direct anchor download if fetch fails.
 * DAM CDN URLs are rewritten to /optidam before fetching.
 */
async function downloadFile(url, signal, preferredFilename) {
    const resolved = prepareDownloadUrl(url);
    if (!resolved)
        return;
    const filename = resolveDownloadFilename(null, resolved, preferredFilename);
    try {
        await fetchAndSaveDownload(resolved, signal, preferredFilename);
    }
    catch (error) {
        devLogger.log("MultiLanguageDocumentDownload: fetch failed, using anchor fallback", error);
        await delay(ANCHOR_FALLBACK_SETTLE_MS);
        clickAnchorFallback(resolved, filename);
    }
}
export const MultiLanguageDocumentDownload = (element, _props) => {
    const toggleButton = element.querySelector('[data-document-download-toggle]');
    const dropdownMenu = element.querySelector('[data-document-download-menu]');
    const chevron = element.querySelector('[data-action-download-chevron]');
    const downloadButton = element.querySelector('[data-document-download-link]');
    const menuItems = dropdownMenu?.querySelectorAll('[data-document-download-menu-link]');
    const previewMenuLinks = dropdownMenu?.querySelectorAll('a[role="menuitem"][target="_blank"]');
    const isMultiLanguage = Boolean(toggleButton && dropdownMenu && chevron);
    const isSingleLanguageDownload = !isMultiLanguage && Boolean(downloadButton);
    const isSingleLanguagePreview = !isMultiLanguage &&
        !downloadButton &&
        Boolean(element.querySelector('a[target="_blank"][href]'));
    if (!isMultiLanguage && !isSingleLanguageDownload && !isSingleLanguagePreview) {
        devLogger.log("Required elements not found - need either download button, preview link (single language), or dropdown elements (multi-language)");
        return;
    }
    let isDropdownOpen = false;
    // Custom event name for coordinating between multiple dropdown instances
    const DROPDOWN_OPEN_EVENT = "multilanguage-dropdown-open";
    const setDropdownState = (open, skipEvent = false) => {
        if (!isMultiLanguage || !dropdownMenu || !toggleButton)
            return;
        isDropdownOpen = open;
        if (open) {
            // Dispatch custom event to notify other dropdowns to close (unless told to skip)
            if (!skipEvent) {
                const event = new CustomEvent(DROPDOWN_OPEN_EVENT, {
                    detail: { element },
                    bubbles: true,
                });
                document.dispatchEvent(event);
            }
            dropdownMenu.classList.remove("hidden");
            toggleButton.setAttribute("data-expanded", "true");
            toggleButton.setAttribute("aria-expanded", "true");
            // Attach click-outside handler after a short delay to avoid immediate triggering
            setTimeout(() => {
                document.addEventListener("click", handleClickOutside, { once: false });
            }, 0);
        }
        else {
            dropdownMenu.classList.add("hidden");
            toggleButton.setAttribute("data-expanded", "false");
            toggleButton.setAttribute("aria-expanded", "false");
            // Clear any pending mouse leave timeout
            if (mouseLeaveTimeout) {
                clearTimeout(mouseLeaveTimeout);
                mouseLeaveTimeout = null;
            }
            // Remove click-outside handler when closed
            document.removeEventListener("click", handleClickOutside);
        }
    };
    const toggleDropdown = () => {
        setDropdownState(!isDropdownOpen);
    };
    const closeDropdown = () => {
        if (!isDropdownOpen)
            return;
        setDropdownState(false);
    };
    const handleToggleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleDropdown();
    };
    const handleClickOutside = (e) => {
        // Use composedPath to check all elements in the event path
        const path = e.composedPath();
        // Check if clicking inside this component
        const clickedInsideComponent = path.includes(element);
        // Don't close if clicking inside this component
        if (clickedInsideComponent) {
            return;
        }
        // Close if clicking outside
        closeDropdown();
    };
    const handleEscapeKey = (e) => {
        if (e.key === "Escape" && isDropdownOpen && toggleButton) {
            closeDropdown();
            toggleButton.focus();
        }
    };
    const handleDropdownOpenEvent = (e) => {
        const customEvent = e;
        // If another dropdown is opening, close this one
        if (customEvent.detail?.element !== element && isDropdownOpen) {
            closeDropdown();
        }
    };
    let mouseLeaveTimeout = null;
    let isDownloading = false;
    const setTriggerBusy = (triggerElement, busy) => {
        triggerElement.setAttribute("aria-busy", busy ? "true" : "false");
        if (triggerElement instanceof HTMLButtonElement) {
            triggerElement.disabled = busy;
        }
    };
    const hideLoadingSpinner = (triggerElement) => {
        const downloadIcon = triggerElement.querySelector("[data-download-icon]");
        const spinner = triggerElement.querySelector("[data-loading-spinner]");
        if (downloadIcon && spinner) {
            spinner.classList.add("hidden");
            downloadIcon.classList.remove("hidden");
        }
    };
    const showLoadingSpinner = (triggerElement) => {
        const downloadIcon = triggerElement.querySelector("[data-download-icon]");
        const spinner = triggerElement.querySelector("[data-loading-spinner]");
        if (downloadIcon && spinner) {
            downloadIcon.classList.add("hidden");
            spinner.classList.remove("hidden");
        }
    };
    const runDownloadWithSpinner = async (triggerElement, url, preferredFilename) => {
        if (isDownloading) {
            return;
        }
        const resolved = prepareDownloadUrl(url);
        if (!resolved)
            return;
        isDownloading = true;
        setTriggerBusy(triggerElement, true);
        const startedAt = performance.now();
        showLoadingSpinner(triggerElement);
        await waitForNextPaint();
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);
        try {
            await downloadFile(url, controller.signal, preferredFilename);
        }
        catch (error) {
            devLogger.log("MultiLanguageDocumentDownload: download failed", error);
        }
        finally {
            window.clearTimeout(timeoutId);
            const remaining = MIN_SPINNER_VISIBLE_MS - (performance.now() - startedAt);
            if (remaining > 0) {
                await delay(remaining);
            }
            hideLoadingSpinner(triggerElement);
            setTriggerBusy(triggerElement, false);
            isDownloading = false;
        }
    };
    const handleDownloadClick = (e, triggerElement) => {
        e.preventDefault();
        const url = triggerElement.getAttribute("data-download-url");
        if (!url) {
            return;
        }
        const filename = triggerElement.getAttribute("data-download-filename");
        void runDownloadWithSpinner(triggerElement, url, filename);
    };
    const handleMouseLeave = (e) => {
        if (!isDropdownOpen || !dropdownMenu)
            return;
        // Clear any existing timeout
        if (mouseLeaveTimeout) {
            clearTimeout(mouseLeaveTimeout);
            mouseLeaveTimeout = null;
        }
        // Check if mouse is moving to the dropdown menu
        const relatedTarget = e.relatedTarget;
        const isMovingToMenu = relatedTarget && (dropdownMenu.contains(relatedTarget) ||
            dropdownMenu === relatedTarget);
        // If moving to menu, don't close
        if (isMovingToMenu) {
            return;
        }
        // Check if mouse is currently over the menu (accounting for the gap)
        const checkMouseOverMenu = () => {
            // Use document.elementFromPoint to check current mouse position
            const mouseX = e.clientX;
            const mouseY = e.clientY;
            const elementUnderMouse = document.elementFromPoint(mouseX, mouseY);
            if (elementUnderMouse && (dropdownMenu.contains(elementUnderMouse) ||
                dropdownMenu === elementUnderMouse ||
                element.contains(elementUnderMouse))) {
                // Mouse is still over component or menu, don't close
                return;
            }
            // Mouse has left, close the dropdown
            closeDropdown();
        };
        // Add delay to allow mouse to reach menu through the gap (30px padding-top)
        // Use a small timeout to check if mouse reaches the menu
        mouseLeaveTimeout = setTimeout(() => {
            checkMouseOverMenu();
            mouseLeaveTimeout = null;
        }, 100);
    };
    const handleMenuMouseEnter = () => {
        // Cancel any pending close when mouse enters menu
        if (mouseLeaveTimeout) {
            clearTimeout(mouseLeaveTimeout);
            mouseLeaveTimeout = null;
        }
    };
    const handleMenuMouseLeave = () => {
        // Close dropdown when mouse leaves the menu
        if (isDropdownOpen) {
            closeDropdown();
        }
    };
    // Initialize aria attributes (only for multi-language)
    if (isMultiLanguage && toggleButton) {
        toggleButton.setAttribute("aria-expanded", "false");
        toggleButton.setAttribute("aria-haspopup", "true");
        toggleButton.setAttribute("aria-label", "Select language");
    }
    // Add role attributes for accessibility
    if (dropdownMenu) {
        dropdownMenu.setAttribute("role", "menu");
    }
    const menuItemClickHandlers = new Map();
    const previewMenuClickHandlers = new Map();
    if (menuItems) {
        menuItems.forEach((item) => {
            item.setAttribute("role", "menuitem");
            const handler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const menuUrl = item.getAttribute("data-download-url");
                if (!menuUrl) {
                    return;
                }
                const menuFilename = item.getAttribute("data-download-filename");
                closeDropdown();
                const downloadTrigger = downloadButton ?? item;
                void runDownloadWithSpinner(downloadTrigger, menuUrl, menuFilename);
            };
            menuItemClickHandlers.set(item, handler);
            item.addEventListener("click", handler);
        });
    }
    if (previewMenuLinks) {
        previewMenuLinks.forEach((link) => {
            const handler = () => {
                closeDropdown();
            };
            previewMenuClickHandlers.set(link, handler);
            link.addEventListener("click", handler);
        });
    }
    // Event listeners (only for multi-language)
    if (isMultiLanguage && toggleButton) {
        toggleButton.addEventListener("click", handleToggleClick);
    }
    // Note: click outside handler is dynamically attached/removed when dropdown opens/closes
    if (isMultiLanguage) {
        document.addEventListener("keydown", handleEscapeKey);
        document.addEventListener(DROPDOWN_OPEN_EVENT, handleDropdownOpenEvent);
        element.addEventListener("mouseleave", handleMouseLeave);
        if (dropdownMenu) {
            dropdownMenu.addEventListener("mouseenter", handleMenuMouseEnter);
            dropdownMenu.addEventListener("mouseleave", handleMenuMouseLeave);
        }
    }
    // Add download button click handler
    let downloadButtonHandler = null;
    if (downloadButton) {
        downloadButtonHandler = (e) => {
            handleDownloadClick(e, downloadButton);
        };
        downloadButton.addEventListener("click", downloadButtonHandler);
    }
    // Cleanup function
    return () => {
        // Clear any pending timeouts
        if (mouseLeaveTimeout) {
            clearTimeout(mouseLeaveTimeout);
            mouseLeaveTimeout = null;
        }
        if (isMultiLanguage && toggleButton) {
            toggleButton.removeEventListener("click", handleToggleClick);
        }
        if (isMultiLanguage) {
            document.removeEventListener("click", handleClickOutside); // Clean up if still attached
            document.removeEventListener("keydown", handleEscapeKey);
            document.removeEventListener(DROPDOWN_OPEN_EVENT, handleDropdownOpenEvent);
            element.removeEventListener("mouseleave", handleMouseLeave);
            if (dropdownMenu) {
                dropdownMenu.removeEventListener("mouseenter", handleMenuMouseEnter);
                dropdownMenu.removeEventListener("mouseleave", handleMenuMouseLeave);
            }
        }
        if (downloadButton && downloadButtonHandler) {
            downloadButton.removeEventListener("click", downloadButtonHandler);
        }
        menuItemClickHandlers.forEach((handler, item) => {
            item.removeEventListener("click", handler);
        });
        previewMenuClickHandlers.forEach((handler, link) => {
            link.removeEventListener("click", handler);
        });
    };
};
